import { DownOutlined, SortDescendingOutlined } from '@ant-design/icons'
import { UnorderedListOutlined } from '@ant-design/icons'
import { Dropdown, List, Menu, Spin } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'

import { useGetNewestsGroupsQuery, useGetViewerQuery } from '@/generated/graphql'
import { DiscoverGroupItem } from '@/modules/group/group-card/group-card.component'

import s from './all-groups.module.less'

interface CategoricalGroupProps {
  categoryId: number | null
  search: string
  type: 'all' | 'viewer'
}

export const CategoricalGroup: React.FC<CategoricalGroupProps> = ({ categoryId, search, type }) => {
  const fetchCount = 24
  const isViewerGroups = type === 'viewer'
  const { t } = useTranslation()
  const [sortBy, setSortBy] = React.useState<string>('postCount')
  const pagination = {
    last: search ? 6 : fetchCount,
    ...(!search && { sortBy }),
  }

  const { data: viewerData } = useGetViewerQuery()

  const { data, fetchMore, loading } = useGetNewestsGroupsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      withPosts: true,
      withFollowers: true,
      pagination,
      filter: {
        ...(search && { search }),
        ...(!search && { categoryId }),
        ...(isViewerGroups && viewerData && { followerUserId: viewerData.viewer.userId }),
      },
    },
  })

  React.useEffect(() => {
    setSortBy('postCount')
  }, [categoryId])

  const groups = (data ? data.groups.edges : [])
    .map(({ node }) => node)
    .slice()
    .reverse()

  const pageInfo = data
    ? data.groups.pageInfo
    : {
        startCursor: null,
        endCursor: null,
        hasPreviousPage: false,
        hasNextPage: false,
      }

  const sortMenu = (
    <Menu selectedKeys={[sortBy]}>
      <Menu.Item key='createdAt' onClick={() => setSortBy('createdAt')}>
        {t(`group.createdAt`)}
      </Menu.Item>
      <Menu.Item key='likedCount' onClick={() => setSortBy('likedCount')}>
        {t(`group.likedCount`)}
      </Menu.Item>
      <Menu.Item key='postCount' onClick={() => setSortBy('postCount')}>
        {t(`group.postCount`)}
      </Menu.Item>
    </Menu>
  )

  const sortDropdown = (
    <Dropdown overlay={sortMenu} trigger={['click']}>
      <a>
        <SortDescendingOutlined style={{ marginRight: 4 }} />
        {t(`group.${sortBy}`)} <DownOutlined />
      </a>
    </Dropdown>
  )

  const loadingComponent = loading && (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Spin delay={300} size='large' />
    </div>
  )

  return (
    <div className={s.container}>
      <div className={s.listHeader}>
        <div className={s.title}>
          <UnorderedListOutlined style={{ marginRight: 8, color: '#48bbb5', fontSize: 22 }} />
          {t('groups.grouplist.title')}
        </div>
        <div className={s.sort}>{sortDropdown}</div>
      </div>
      <InfiniteScroll
        dataLength={groups.length}
        loader={loadingComponent}
        hasMore={!search && pageInfo.hasPreviousPage}
        next={() =>
          fetchMore({
            variables: {
              pagination: {
                ...pagination,
                before: pageInfo.startCursor,
              },
            },
          })
        }
      >
        <div className={s.groupList}>
          <List split={false}>
            {groups.map((g) => (
              <DiscoverGroupItem key={g.groupId} group={g} />
            ))}
          </List>
        </div>
      </InfiniteScroll>
    </div>
  )
}
